import Style from './panelStyles.module.scss';
import { useNode } from '@craftjs/core';
import { ReactComponent as PlusIcon } from 'assets/img/generalBuilder/plus_24.svg';
import { ReactComponent as Dots } from 'assets/img/generalBuilder/dots_24.svg';
import { useUploadPicture } from '../../helpers/loadResourceComponent/useUploadResourceModal';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { FormImageProps } from '../../types/Image';
import { useCallback } from 'react';
import { ConfirmResult } from 'src/components/dialogModal/useConfirmationModal';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { AppButton } from 'uikit/buttons';
import { useFieldAttachments } from 'src/features/generalForms/context/attachments.provider';
import { DropDownMenu } from 'uikit/dropDownMenu/dropDownMenu.component';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { downloadFile, getFileResponse } from 'src/helpers/file-helper';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';

export const ImageBlockPanel = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.ImageBlock');
  const { props } = useNode((node) => ({
    props: node.data.props as FormImageProps,
  }));
  const { onChange, attachment } = useFieldAttachments({ preventDownloading: true });

  const pictureModal = useUploadPicture();
  const handleUploadImage = useCallback(async () => {
    const result = await pictureModal.open(attachment ? [attachment] : []);
    if (result.result === ConfirmResult.Confirm) onChange(result.data);
  }, [attachment, onChange, pictureModal]);

  const downloadHandler = useCallback(async () => {
    try {
      if (!attachment) throw new Error(t('LoadingError'));
      const localFile = {
        file: attachment.file,
        fileName: attachment.fileName,
        id: attachment.fileId,
      } satisfies AppAttachmentValue;

      const response = getFileResponse(localFile);
      if (response) downloadFile(response);
    } catch (error) {
      showServerErrorToast(error);
    }
  }, [attachment, t]);

  const menuItems: DropDownMenuOption[] = [
    {
      key: 0,
      text: t('Panel.EditButton'),
      action: handleUploadImage,
      variant: 'primary',
    },
    {
      key: 1,
      text: t('Panel.DownloadButton'),
      action: downloadHandler,
      variant: 'primary',
    },
    {
      key: 2,
      text: '',
      variant: 'divider',
    },
    {
      key: 3,
      text: t('Panel.DeleteButton'),
      action: () => onChange(null),
      variant: 'danger',
    },
  ];

  return (
    <PanelContainer header={t('Panel.Header')} panelName={'General'}>
      <ControlsGroup contentClassName={Style.flexContainer}>
        <div>
          {!!props.attachment?.fileName ? (
            <div className={Style.imagePanelFileWithMenu}>
              <span>{props.attachment?.fileName}</span>
              <DropDownMenu
                Icon={Dots}
                options={menuItems}
                colorSchema={'basic'}
                className={Style.visibility}
                testId={'issue-card-menu'}
              />
            </div>
          ) : (
            <AppButton
              variant={'icon-link'}
              colorSchema={'primary'}
              Icon={PlusIcon}
              text={t('Panel.AddButton')}
              onClick={handleUploadImage}
            />
          )}
          {pictureModal.modal}
        </div>
      </ControlsGroup>
    </PanelContainer>
  );
};
